import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Download, FileText, ArrowLeft, CheckCircle2, Phone, Mail, User,
  ClipboardCheck, ShieldCheck, Thermometer, Users, Search, Bug, Utensils
} from "lucide-react";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";

const API = process.env.REACT_APP_BACKEND_URL;

const checklistIcons = [ClipboardCheck, ShieldCheck, Thermometer, Users, Search, Bug, Utensils];

const checklistDescriptions = [
  "Daily routine checklist for maintaining hygiene standards in commercial kitchens.",
  "Ensure your food business meets FSSAI requirements before inspection.",
  "Monitor and maintain proper food storage and temperature standards.",
  "Ensure all kitchen staff follow hygiene and grooming best practices.",
  "Prepare your outlet for a food safety audit with this self-assessment guide.",
  "Prevent and monitor pest activity in food preparation and storage areas.",
  "Prevent cross-contamination and ensure safe food handling at every step.",
];

export default function ChecklistsPage() {
  const [checklists, setChecklists] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [downloadToken, setDownloadToken] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/checklists`).then(r => setChecklists(r.data)).catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/checklist-leads`, form);
      setDownloadToken(data.download_token);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadAll = () => {
    window.open(`${API}/api/checklists/download-all?token=${downloadToken}`, "_blank");
  };

  const downloadSingle = (id) => {
    window.open(`${API}/api/checklists/download/${id}?token=${downloadToken}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="checklists-page">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#E3E8DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/d8a36d0e-acf5-4772-b845-b74bc2191557/images/0a9bc1777adf6f3c4201784effdb03c09d13cbe1b10ecbb42fe5c5571c07a9f7.png"
              alt="Food Safety Partners Logo"
              className="w-14 h-14 object-contain"
            />
            <div className="leading-tight">
              <span className="font-['Outfit'] font-bold text-[#16231A] text-sm block">Food Safety</span>
              <span className="text-[10px] text-[#526B57] tracking-wider uppercase">Partners</span>
            </div>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-[#526B57] hover:text-[#7CA63A] transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft size={16} />
            Back to website
          </Link>
        </div>
      </nav>

      <main className="pt-16">
        {/* Hero */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="inline-block text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] bg-[#EAF0E2] px-4 py-2 rounded-full mb-4">
                Free Resources
              </span>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#16231A] tracking-tight leading-[1.1] mb-4">
                Free Food Safety <span className="text-[#7CA63A]">Checklists</span>
              </h1>
              <p className="text-[#526B57] max-w-xl mx-auto leading-relaxed">
                Download 7 ready-to-use checklists covering kitchen hygiene, FSSAI compliance, food storage, staff grooming, pest control and more.
              </p>
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Checklist Cards */}
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {checklists.map((cl, i) => {
                  const Icon = checklistIcons[i] || FileText;
                  return (
                    <motion.div
                      key={cl.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.05 * i }}
                      className="bg-white border border-[#E3E8DC] rounded-2xl p-6 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300 group"
                      data-testid={`checklist-card-${i}`}
                    >
                      <div className="w-10 h-10 bg-[#EAF0E2] rounded-xl flex items-center justify-center mb-3 group-hover:bg-[#7CA63A] transition-colors duration-300">
                        <Icon size={18} className="text-[#7CA63A] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h3 className="font-['Outfit'] text-sm font-bold text-[#16231A] mb-1">{cl.title}</h3>
                      <p className="text-xs text-[#526B57] leading-relaxed mb-3">{checklistDescriptions[i] || ""}</p>
                      <div className="flex items-center gap-1.5 text-xs text-[#7CA63A] font-medium">
                        <FileText size={12} />
                        PDF Download
                      </div>
                      {downloadToken && (
                        <button
                          onClick={() => downloadSingle(cl.id)}
                          className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs bg-[#EAF0E2] text-[#2B4131] py-2 rounded-lg hover:bg-[#DCE6CF] transition-colors"
                          data-testid={`download-single-${i}`}
                        >
                          <Download size={12} />
                          Download
                        </button>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Lead Capture Form */}
            <div className="lg:col-span-2">
              <div className="sticky top-24">
                {downloadToken ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white border border-[#E3E8DC] rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center"
                    data-testid="download-success"
                  >
                    <div className="w-14 h-14 bg-[#7CA63A] rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 size={24} className="text-white" />
                    </div>
                    <h3 className="font-['Outfit'] text-xl font-bold text-[#16231A] mb-2">Your checklists are ready!</h3>
                    <p className="text-sm text-[#526B57] mb-6">Download all 7 checklists as a single ZIP file, or pick individual ones from the cards.</p>
                    
                    <button
                      onClick={downloadAll}
                      className="w-full bg-[#7CA63A] text-white py-3.5 rounded-full font-medium hover:bg-[#68902A] transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2"
                      data-testid="download-all-btn"
                    >
                      <Download size={18} />
                      Download All (ZIP)
                    </button>
                  </motion.div>
                ) : (
                  <div className="bg-white border border-[#E3E8DC] rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="checklist-lead-form">
                    <h3 className="font-['Outfit'] text-lg font-bold text-[#16231A] mb-1">Get Free Checklists</h3>
                    <p className="text-sm text-[#526B57] mb-6">Enter your details to download all 7 checklists instantly.</p>
                    
                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4" data-testid="checklist-form-error">
                        {error}
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Your Name *</label>
                        <div className="relative">
                          <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#526B57]" />
                          <input
                            name="name"
                            type="text"
                            value={form.name}
                            onChange={handleChange}
                            required
                            placeholder="Enter your name"
                            className="w-full pl-9 pr-4 py-3 bg-[#F7F8F5] border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                            data-testid="checklist-name-input"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Phone Number *</label>
                        <div className="relative">
                          <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#526B57]" />
                          <input
                            name="phone"
                            type="tel"
                            value={form.phone}
                            onChange={handleChange}
                            required
                            placeholder="Enter your phone number"
                            className="w-full pl-9 pr-4 py-3 bg-[#F7F8F5] border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                            data-testid="checklist-phone-input"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-2 block">Email Address *</label>
                        <div className="relative">
                          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#526B57]" />
                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            placeholder="Enter your email"
                            className="w-full pl-9 pr-4 py-3 bg-[#F7F8F5] border border-[#E3E8DC] rounded-xl text-sm text-[#16231A] focus:outline-none focus:border-[#7CA63A] focus:ring-1 focus:ring-[#7CA63A] transition-colors"
                            data-testid="checklist-email-input"
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#7CA63A] text-white py-3.5 rounded-full font-medium hover:bg-[#68902A] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="checklist-submit-btn"
                      >
                        <Download size={16} />
                        {submitting ? "Processing..." : "Download Free Checklists"}
                      </button>

                      <p className="text-[10px] text-[#526B57] text-center">
                        By downloading, you agree to receive updates from Food Safety Partners. We respect your privacy.
                      </p>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#16231A] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs text-[#8BA18F]">&copy; {new Date().getFullYear()} Food Safety Partners. All rights reserved.</p>
        </div>
      </footer>

      <WhatsAppFloat />
    </div>
  );
}
